import React from 'react';
import {
    AmbientLight,
    DirectionalLight,
    MeshLambertMaterial,
    OctahedronBufferGeometry,
    Vector3,
    Scene,
    PerspectiveCamera,
    WebGLRenderer,
    MeshStandardMaterial,
    Color,
    SphereBufferGeometry,
    Mesh
} from "three";
import {
    propagate,
    SatRec,
    gstime,
    eciToGeodetic,
    EciVec3
} from 'satellite.js';
import { OrbitControls } from 'three-orbitcontrols-ts';
import { ThemeContext, ThemeContextType } from '../../../pages/_app';
import { preProcessedTLE } from './preProcessedTLE.js';
import { SatData } from './GlobeTypes';
import styles from './GlobeAnimation.module.scss';

interface Props {
    height: number;
    width: number;
}

const TLE_DATA_DATE = new Date(2018, 0, 26);
const TIME_SCALE = 33; // per frame
const RADIUS = 228;
const EARTH_RADIUS = 6371;

function satrecToXYZ(satrec: SatRec, date: Date): number[] {
    const positionAndVelocity = propagate(satrec, date);
    const gmst = gstime(date);
    const positionGd = eciToGeodetic(positionAndVelocity.position as EciVec3<number>, gmst);
    return [positionGd.longitude, positionGd.latitude, positionGd.height];
}

function satelliteVector(satrec: SatRec, date: Date): Vector3 {
    const xyz = satrecToXYZ(satrec, date);
    const lambda = xyz[0];
    const phi = xyz[1];
    const cosPhi = Math.cos(phi);
    const r = ((xyz[2] + EARTH_RADIUS) / EARTH_RADIUS) * RADIUS;
    return new Vector3(
        r * cosPhi * Math.cos(lambda),
        r * cosPhi * Math.sin(lambda),
        r * Math.sin(phi)
    );
}

const GlobeAnimation: React.FC<Props> = (props) => {
    const { modeClassName } = React.useContext(ThemeContext) as ThemeContextType;
    const scene = React.useMemo(() => new Scene, []);
    const camera = React.useMemo(() => new PerspectiveCamera(70, props.width / props.height, 1, 10000), []);
    const renderer = React.useMemo(() => new WebGLRenderer(), []);
    const controls = React.useMemo(() => new OrbitControls(camera, renderer.domElement), []);
    const globeMaterial = React.useMemo(() => new MeshStandardMaterial({ color: 0xE7D4FF, wireframe: true }), []);
    const sphere = React.useMemo(() => new Mesh(new SphereBufferGeometry(RADIUS - 0.5, 36, 36), globeMaterial), []);
    const ref = React.useRef<HTMLDivElement>(null);
    const animRef = React.useRef(0);
    //const satellites = React.useRef<Points>(new Points);
    const parsedTles = React.useMemo<SatData[]>(() => preProcessedTLE as SatData[], []);
    const previousTimeRef = React.useRef(0);
    const satelliteGeometries = React.useRef<Mesh[]>([]);
    const [theme, setTheme] = React.useState({
        sat: 0x00DDC2,
        wire: 0xE7D4FF,
        atmo: 0xF4F4F4
    })

    const handleThemeChange = function () {
        //light mode vs dark mode
        let newTheme = {
            sat: 0x00DDC2,
            wire: 0xE7D4FF,
            atmo: 0xF4F4F4
        };
        if (modeClassName !== 'light') {
            newTheme = {
                sat: 0x00B6CF,
                wire: 0x6A45B8,
                atmo: 0x262626
            };
        }
        for (let i = 0; i < satelliteGeometries.current.length; i++) {
            //@ts-expect-error not typed correctly
            satelliteGeometries.current[i].material.color.setHex(newTheme.sat);
        }
        setTheme(newTheme);
        globeMaterial.color = new Color(newTheme.wire);
        renderer.setClearColor(newTheme.atmo, 1);
    }

    const animate = (time: number) => {
        const newDate = new Date(TLE_DATA_DATE.getTime() + Math.floor(time * TIME_SCALE));
        for (let i = 0; i < parsedTles.length; i++) {
            const newVectors = satelliteVector(parsedTles[i].satrec, newDate);
            satelliteGeometries.current[i].position.setX(newVectors.x);
            satelliteGeometries.current[i].position.setY(newVectors.y);
            satelliteGeometries.current[i].position.setZ(newVectors.z);
            //satelliteGeometries.current[i].geometry.attributes.position.setXYZ(0, newVectors.x, newVectors.y, newVectors.z);
            //satelliteGeometries.current[i].geometry.attributes.position.needsUpdate = true;
            //satellites.current.geometry.attributes.position.setXYZ(i, newVectors.x, newVectors.y, newVectors.z);
        }
        sphere.rotation.y += 0.0003;
        controls.update();
        previousTimeRef.current = time;
        //satellites.current.geometry.attributes.position.needsUpdate = true;
        renderer.render(scene, camera);
        animRef.current = requestAnimationFrame(animate);
    }

    React.useEffect(() => {
        //resize
        renderer.setSize(props.width, props.height);
    }, [props.width, props.height]);

    React.useEffect(() => {
        //handle theme change
        handleThemeChange();
    }, [modeClassName]);

    React.useEffect(() => {
        if (parsedTles.length > 0) {
            ref.current?.appendChild(renderer.domElement);


            renderer.setPixelRatio(window.devicePixelRatio);
            renderer.setSize(props.width, props.height);

            if (controls) {
                controls.autoRotate = false;
                controls.enableZoom = false;
                controls.enableRotate = true;
                controls.enablePan = false;
                controls.enableDamping = true;
                controls.dampingFactor = .07;
                controls.rotateSpeed = 0.02;

                camera.position.set(300, 300, 300);
                controls.update();
            }

            const ambientLight = new AmbientLight(0xbbbbbb);
            const directionalLight = new DirectionalLight(0x5548ED, 0.6);
            directionalLight.position.x += 2;

            scene.add(sphere);
            scene.add(ambientLight);
            scene.add(directionalLight);

            const points: Vector3[] = parsedTles.map(function (d) { return satelliteVector(d.satrec, TLE_DATA_DATE); });

            const satGeometries = points.map(d => {
                const satGeometry = new OctahedronBufferGeometry(1.75, 0);
                const satMaterial = new MeshLambertMaterial({ color: theme.sat });
                const satMesh = new Mesh(satGeometry, satMaterial);
                satMesh.position.setX(d.x);
                satMesh.position.setY(d.y);
                satMesh.position.setZ(d.z);
                return satMesh
            });
            satelliteGeometries.current = satGeometries;

            //const satGeometry = new BufferGeometry();
            //satGeometry.setFromPoints(points);

            //satellites.current = new Points(satGeometry, new PointsMaterial({ color: 0x00CEEA, size: 4 }));
            satelliteGeometries.current.forEach(mesh => scene.add(mesh));
            //scene.add(satellites.current);
            renderer.render(scene, camera);

            animRef.current = requestAnimationFrame(animate);
            return () => cancelAnimationFrame(animRef.current);
        }
    }, [parsedTles])

    return (
        <div ref={ref} className={styles['viz-container']} />
    )
}
export default GlobeAnimation
